import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiX } from 'react-icons/fi';
import integrationService from '../../services/integrationService';

const IntegrationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    integration_type: 'api',
    provider: '',
    description: '',
    status: 'pending',
    endpoint_url: '',
    auth_method: 'none',
    sync_frequency: 'manual',
    is_enabled: true,
    configuration: {},
    credentials: {},
    metadata: {}
  });
  const [jsonFields, setJsonFields] = useState({
    configuration: '',
    credentials: '',
    metadata: ''
  });

  useEffect(() => {
    fetchIntegration();
  }, [id]);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const data = await integrationService.getIntegrationById(id);
      const integration = data.integration;
      setFormData(integration);
      
      // Convert JSON objects to strings for textarea
      setJsonFields({
        configuration: JSON.stringify(integration.configuration || {}, null, 2),
        credentials: JSON.stringify(integration.credentials || {}, null, 2),
        metadata: JSON.stringify(integration.metadata || {}, null, 2)
      });
    } catch (error) {
      toast.error('Failed to fetch integration');
      console.error('Error fetching integration:', error);
      navigate('/app/integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleJsonChange = (field, value) => {
    setJsonFields(prev => ({
      ...prev,
      [field]: value
    }));

    try {
      const parsed = value ? JSON.parse(value) : {};
      setFormData(prev => ({
        ...prev,
        [field]: parsed
      }));
    } catch (error) {
      // Invalid JSON, don't update formData
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Validate JSON fields
      try {
        JSON.parse(jsonFields.configuration || '{}');
        JSON.parse(jsonFields.credentials || '{}');
        JSON.parse(jsonFields.metadata || '{}');
      } catch (error) {
        toast.error('Invalid JSON in configuration fields');
        setSaving(false);
        return;
      }

      await integrationService.updateIntegration(id, formData);
      toast.success('Integration updated successfully');
      navigate('/app/integrations');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update integration');
      console.error('Error updating integration:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Integration</h1>
        <p className="mt-1 text-sm text-gray-500">
          Update integration settings and configuration
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Integration Type *
              </label>
              <select
                name="integration_type"
                value={formData.integration_type}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="api">API</option>
                <option value="webhook">Webhook</option>
                <option value="oauth">OAuth</option>
                <option value="database">Database</option>
                <option value="file_sync">File Sync</option>
                <option value="messaging">Messaging</option>
                <option value="calendar">Calendar</option>
                <option value="crm">CRM</option>
                <option value="accounting">Accounting</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Provider
              </label>
              <input
                type="text"
                name="provider"
                value={formData.provider || ''}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={handleChange}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Connection Settings */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Connection Settings</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Endpoint URL
              </label>
              <input
                type="url"
                name="endpoint_url"
                value={formData.endpoint_url || ''}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Authentication Method
              </label>
              <select
                name="auth_method"
                value={formData.auth_method}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="none">None</option>
                <option value="api_key">API Key</option>
                <option value="oauth2">OAuth 2.0</option>
                <option value="basic">Basic Auth</option>
                <option value="bearer_token">Bearer Token</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Sync Frequency
              </label>
              <select
                name="sync_frequency"
                value={formData.sync_frequency}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="realtime">Real-time</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="error">Error</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_enabled"
                checked={formData.is_enabled}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Enable integration
              </label>
            </div>
          </div>
        </div>

        {/* Sync Information */}
        {formData.last_sync_at && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Sync Information</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Sync</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(formData.last_sync_at).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Sync Status</label>
                <p className="mt-1 text-sm text-gray-900 capitalize">
                  {formData.last_sync_status || 'N/A'}
                </p>
              </div>
              {formData.error_message && (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Error Message</label>
                  <p className="mt-1 text-sm text-red-600">{formData.error_message}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Advanced Configuration */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Advanced Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Configuration (JSON)
              </label>
              <textarea
                value={jsonFields.configuration}
                onChange={(e) => handleJsonChange('configuration', e.target.value)}
                rows={6}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Integration-specific configuration as JSON
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Credentials (JSON)
              </label>
              <textarea
                value={jsonFields.credentials}
                onChange={(e) => handleJsonChange('credentials', e.target.value)}
                rows={6}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Authentication credentials as JSON (will be encrypted)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Metadata (JSON)
              </label>
              <textarea
                value={jsonFields.metadata}
                onChange={(e) => handleJsonChange('metadata', e.target.value)}
                rows={6}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Additional metadata as JSON
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/app/integrations')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <FiX className="mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <FiSave className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IntegrationEdit;

